import {
  useDeleteUserMutation,
  useGetUsersQuery,
} from "../../slices/usersApiSlice";

import { toast } from "react-toastify";
import { FaTrash, FaEdit, FaUserPlus, FaKey } from "react-icons/fa";
import { Link } from "react-router-dom";

const UserListScreen = () => {
  const { data: users, refetch, isLoading, error } = useGetUsersQuery();

  const [deleteUser] = useDeleteUserMutation();

  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser(id).unwrap();

        refetch();

        toast.success("User deleted successfully");
      } catch (err) {
        toast.error(
          err?.data?.message || err?.error || "Failed to delete user",
        );
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* ==========================================
            HEADER
        ========================================== */}

        <div className="mb-8 bg-white/80 backdrop-blur-lg rounded-3xl shadow-lg p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            {/* Title */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                Coaching Center User Management
              </h1>

              <p className="text-gray-500 mt-2">
                Manage users belonging to your coaching center
              </p>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Forgot Password Requests */}
              <Link
                to="/users/forgot-password-requests"
                className="inline-flex items-center justify-center gap-2
      bg-gradient-to-r from-orange-500 to-amber-500
      hover:from-orange-600 hover:to-amber-600
      text-white font-semibold
      px-6 py-3 rounded-xl
      shadow-lg hover:shadow-xl
      transition-all duration-200
      whitespace-nowrap"
              >
                <FaKey />
                Forgot Password Requests
              </Link>

              {/* Create User */}
              <Link
                to="/users/create"
                className="inline-flex items-center justify-center gap-2
      bg-gradient-to-r from-emerald-600 to-teal-600
      hover:from-emerald-700 hover:to-teal-700
      text-white font-semibold
      px-6 py-3 rounded-xl
      shadow-lg hover:shadow-xl
      transition-all duration-200
      whitespace-nowrap"
              >
                <FaUserPlus />
                Create User
              </Link>
            </div>
          </div>
        </div>

        {/* ==========================================
            USER LIST
        ========================================== */}

        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-600">
              Loading coaching center users...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              {error?.data?.message || error?.error}
            </div>
          ) : users?.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              No users found for this coaching center.
            </div>
          ) : (
            <>
              {/* ======================================
                  DESKTOP TABLE
              ====================================== */}

              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left">Name</th>

                      <th className="px-6 py-4 text-left">Email</th>

                      <th className="px-6 py-4 text-left">Phone</th>

                      <th className="px-6 py-4 text-left">Role</th>

                      <th className="px-6 py-4 text-left">Student ID</th>

                      <th className="px-6 py-4 text-left">Batch</th>

                      <th className="px-6 py-4 text-left">Status</th>

                      <th className="px-6 py-4 text-center">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user._id}
                        className="border-b hover:bg-blue-50 transition"
                      >
                        <td className="px-6 py-5 font-semibold text-gray-800">
                          {user.name}
                        </td>

                        <td className="px-6 py-5 text-gray-600">
                          {user.email || "N/A"}
                        </td>

                        <td className="px-6 py-5">{user.phone || "N/A"}</td>

                        <td className="px-6 py-5">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              user.role === "productAdmin"
                                ? "bg-red-100 text-red-700"
                                : user.role === "centerAdmin"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-green-100 text-green-700"
                            }`}
                          >
                            {user.role === "centerAdmin"
                              ? "Center Admin"
                              : user.role}
                          </span>
                        </td>

                        <td className="px-6 py-5">{user.studentId || "N/A"}</td>

                        <td className="px-6 py-5">{user.batch || "N/A"}</td>

                        <td className="px-6 py-5">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              user.isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td className="px-6 py-5">
                          <div className="flex justify-center gap-3">
                            <Link
                              to={`/users/${user._id}/edit`}
                              className="bg-blue-100 hover:bg-blue-600 hover:text-white p-2 rounded-lg transition"
                              title="Edit User"
                            >
                              <FaEdit />
                            </Link>

                            <button
                              onClick={() => deleteHandler(user._id)}
                              className="bg-red-100 hover:bg-red-600 hover:text-white p-2 rounded-lg transition"
                              title="Delete User"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ======================================
                  MOBILE CARDS
              ====================================== */}

              <div className="lg:hidden p-4 space-y-5">
                {users.map((user) => (
                  <div
                    key={user._id}
                    className="bg-white rounded-2xl shadow-md border p-5"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h2 className="font-bold text-lg text-gray-800">
                        {user.name}
                      </h2>

                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.role === "centerAdmin"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {user.role === "centerAdmin"
                          ? "Center Admin"
                          : "Student"}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                      <p>
                        <span className="font-semibold">Email:</span>{" "}
                        {user.email || "N/A"}
                      </p>

                      <p>
                        <span className="font-semibold">Phone:</span>{" "}
                        {user.phone || "N/A"}
                      </p>

                      <p>
                        <span className="font-semibold">Student ID:</span>{" "}
                        {user.studentId || "N/A"}
                      </p>

                      <p>
                        <span className="font-semibold">Batch:</span>{" "}
                        {user.batch || "N/A"}
                      </p>

                      <p>
                        <span className="font-semibold">Course:</span>{" "}
                        {user.course || "N/A"}
                      </p>

                      <p>
                        <span className="font-semibold">Status:</span>{" "}
                        {user.isActive ? "Active" : "Inactive"}
                      </p>
                    </div>

                    <div className="flex justify-end gap-3 mt-5">
                      <Link
                        to={`/users/${user._id}/edit`}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl transition"
                      >
                        <FaEdit />
                        Edit
                      </Link>

                      <button
                        onClick={() => deleteHandler(user._id)}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl transition"
                      >
                        <FaTrash />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserListScreen;
